'use client';

import { useEffect, useRef, useState } from 'react';

import { useCityLocation } from './use-city-location.hook';

import { useSearchParams } from '@/hooks/use-search-params.hook';
import { getUserLocationStorage } from '@/storage/location.storage';
import { setUserLocation, userLocationStore } from '@/store/user-location.store';
import { PersistedUserNavigationSearchParams } from '@/types/enums/persisted-user-navigation-search-params.enum';
import { formatPlaceFromSlug } from '@/utils/formatters/city/format-place-from-slug.const';
import { toCitySlug } from '@/utils/formatters/city/to-city-slug.util';
import { slugify } from '@/utils/formatters/slugfy';

interface UseHydrateUserLocationReturn {
  isLocationReady: boolean;
}

export function useHydrateUserLocation(): UseHydrateUserLocationReturn {
  const { cidade } = useSearchParams([PersistedUserNavigationSearchParams.CIDADE]);
  const { isGoogleMapsReady, selectCity } = useCityLocation();
  const [isLocationReady, setIsLocationReady] = useState(false);

  const selectCityRef = useRef(selectCity);

  const citySlug = cidade ? slugify(cidade) : null;

  useEffect(() => {
    selectCityRef.current = selectCity;
  }, [selectCity]);

  useEffect(() => {
    let isActive = true;

    const storedLocation = userLocationStore.getState().userLocation ?? getUserLocationStorage() ?? undefined;
    const storedSlug = toCitySlug(storedLocation?.city, storedLocation?.state);
    const storedHasCoordinates = Boolean(storedLocation?.latitude && storedLocation?.longitude);

    const hydrateFromStorage = () => {
      if (storedLocation && !userLocationStore.getState().userLocation) {
        setUserLocation(storedLocation);
      }
    };

    function pickCityToResolve(): { city: string; state: string } | undefined {
      const cityFromUrl = citySlug && citySlug !== storedSlug ? formatPlaceFromSlug(citySlug) : undefined;

      if (cityFromUrl?.city && cityFromUrl.state) {
        return { city: cityFromUrl.city, state: cityFromUrl.state };
      }

      if (!storedHasCoordinates && storedLocation?.city && storedLocation.state) {
        return { city: storedLocation.city, state: storedLocation.state };
      }

      return undefined;
    }

    const cityToResolve = pickCityToResolve();

    if (!cityToResolve) {
      hydrateFromStorage();
      setIsLocationReady(true);

      return;
    }

    if (!isGoogleMapsReady) {
      return;
    }

    async function resolveCity(city: string, state: string) {
      try {
        const place = await selectCityRef.current(city, state);

        if (!isActive) {
          return;
        }

        if (!place) {
          hydrateFromStorage();
        }
      } finally {
        if (isActive) {
          setIsLocationReady(true);
        }
      }
    }

    resolveCity(cityToResolve.city, cityToResolve.state);

    return () => {
      isActive = false;
    };
  }, [citySlug, isGoogleMapsReady]);

  return { isLocationReady };
}
